import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { financeService } from '@/services/financeService'
import { areasForRole, type FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import {
  buildPortalTypesByArea,
  isPortalTypeVisibleForRole,
} from '@/components/Financeiro/solicitacoes/portalTypeAccess'
import type { PortalTypeKey } from '@/components/Financeiro/solicitacoes/FinanceRequestFormModal'
import type { Role } from '@/types'

export type FinanceTypeCard = {
  key: string
  area: FinanceAreaKey
  label: string
  slaHours: number
  mvp: boolean
  portal?: boolean
  c4ManualOnly?: boolean
}

export function useFinanceiroSolicitacoesPage() {
  const role = (useAuthStore((s) => s.user?.role) ?? 'bdr') as Role
  const allowedAreas = useMemo(() => areasForRole(role), [role])
  const [types, setTypes] = useState<FinanceTypeCard[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedType, setSelectedType] = useState<PortalTypeKey | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const loadTypes = useCallback(async () => {
    setLoading(true)
    try {
      const data = await financeService.listTypes()
      setTypes(
        data.map((t) => ({
          key: t.key,
          area: t.area as FinanceAreaKey,
          label: t.label,
          slaHours: t.slaHours,
          mvp: Boolean(t.mvp),
          portal: Boolean(t.portal),
          c4ManualOnly: Boolean(t.c4ManualOnly),
        })),
      )
    } catch {
      toast.error('Não foi possível carregar os tipos de solicitação.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadTypes()
  }, [loadTypes])

  const visibleTypes = useMemo(
    () => types.filter((t) => isPortalTypeVisibleForRole(role, t)),
    [types, role],
  )

  const typesByArea = useMemo(
    () => buildPortalTypesByArea(role, visibleTypes),
    [role, visibleTypes],
  )

  const submitRequest = useCallback(
    async (
      typeKey: PortalTypeKey,
      formData: Record<string, string>,
      linkedEmpresaId?: string,
      linkedDealId?: string,
      attachments?: string[],
    ) => {
      setSubmitting(true)
      try {
        const result = await financeService.createRequest({
          type: typeKey,
          linkedEmpresaId,
          linkedDealId,
          formData,
          attachments,
        })
        toast.success(`Solicitação aberta: ${result.protocol}`)
        setSelectedType(null)
        return result
      } catch {
        toast.error('Falha ao enviar solicitação. Tente novamente.')
        return null
      } finally {
        setSubmitting(false)
      }
    },
    [],
  )

  return {
    role,
    allowedAreas,
    loading,
    typesByArea,
    selectedType,
    setSelectedType,
    submitting,
    submitRequest,
  }
}
